import { isEmpty } from 'lodash';
import i18n from '../../../i18n';
import { Image } from '../../../components/image/Image';
import { DetailsLink } from '../../../views/ats-details/details-link';
import styles from './svodService.module.scss';
import { Link } from '../../../components/Link';
import { getAggregatedTitleImage } from '../../../queries/ats/common';
import { SvodProvider } from '../../../queries/ats/me/providers/types';
import { useSectionItems } from '../../../queries/ats/layout/queries';
import { ErrorBoundary } from '../../error-boundary';

interface SvodChannelItemsProps {
  provider: SvodProvider;
}

const SvodChannelItems = ({ provider }: SvodChannelItemsProps) => {
  const {
    data: promoItems,
    isLoading,
    isError,
  } = useSectionItems({
    pageSlug: provider.params.pageSlug,
    sectionSlug: provider.params.promoSectionSlug,
  });

  if (isLoading) {
    return <SvodChannelItems.Skeletons />;
  }

  if (isError) {
    throw new Error(
      'Failed to fetch svod channel items with ' + provider.params.pageSlug + provider.params.promoSectionSlug,
    );
  }

  if (!promoItems || isEmpty(promoItems)) {
    return null;
  }

  return (
    <div className={styles.promotions}>
      {promoItems?.map((promo, index) => (
        <DetailsLink key={promo.id + index} id={promo.id} type={promo.title?.type ?? 'movie'}>
          <button className={styles.promo} key={promo.id + index}>
            <Image alt={promo.name} src={getAggregatedTitleImage(promo.title?.masterId ?? '', 'wide', 300)} />
            <div className={styles.title}>{promo.name}</div>
          </button>
        </DetailsLink>
      ))}
    </div>
  );
};

SvodChannelItems.Skeletons = () => (
  <div className={styles.promotions}>
    {Array.from({ length: 3 }).map((_, index) => {
      return (
        <button className={styles.promo} key={index}>
          <div className={styles.title}>{''}</div>
        </button>
      );
    })}
  </div>
);

interface SvodServiceProps {
  provider: SvodProvider;
}

export default function SvodService({ provider }: SvodServiceProps) {
  return (
    <div className={styles.svodService}>
      <ErrorBoundary>
        <SvodChannelItems provider={provider} />
      </ErrorBoundary>

      <div className={styles.svodLinkContainer}>
        <Link className={styles.svodLink} to={`/${provider.params.pageSlug}`} href={`/${provider.params.pageSlug}`}>
          <span className={styles.svodLinkText}>
            {i18n.t<string>('watch all from')}
            <span className={styles.iconWrapper}>
              <span className={styles.icon}>{'>'}</span>
            </span>
          </span>
          <img crossOrigin="" alt={provider.image} src={provider.image} />
        </Link>
      </div>
    </div>
  );
}
